// Sentry — error + crash reporting.
//
// We use BOTH @sentry/capacitor (which handles native Android crashes) AND
// @sentry/react (which handles JS errors in the WebView and provides the
// ErrorBoundary). The Capacitor SDK wraps the React SDK transparently —
// when running inside a Capacitor app it forwards events to the native
// transport, falling back to the JS transport on plain web.
//
// Reference: https://docs.sentry.io/platforms/javascript/guides/capacitor/
//
// Set VITE_SENTRY_DSN in .env to enable. If unset, init() no-ops with a
// console warning so dev builds don't constantly nag.

import * as Sentry from "@sentry/capacitor";
import * as SentryReact from "@sentry/react";

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENV = import.meta.env.MODE === "production" ? "production" : "development";

let initialised = false;

export function initSentry(): void {
  if (initialised) return;
  if (!DSN) {
    // eslint-disable-next-line no-console
    console.warn("[sentry] VITE_SENTRY_DSN unset — error reporting disabled");
    return;
  }
  Sentry.init(
    {
      dsn: DSN,
      environment: ENV,
      // Don't ship session replays / performance traces by default — those
      // are bundle-size and quota heavy. We can enable selectively later.
      tracesSampleRate: 0,
      // Filter out the noise we know about: offline-induced fetch failures
      // (the app already shows the offline banner), router-cancelled
      // promises, ResizeObserver loop notifications.
      ignoreErrors: [
        /Failed to fetch/i,
        /Load failed/i,
        /NetworkError when attempting to fetch/i,
        /AbortError/i,
        /ResizeObserver loop /i,
        /The user aborted a request/i,
      ],
      beforeSend(event, hint) {
        // Drop events that have no actionable info — usually the side-effect
        // of a third-party iframe or a console.error with no stack.
        const exception = event.exception?.values?.[0];
        if (!exception?.stacktrace && !hint?.originalException) return null;
        return event;
      },
    },
    SentryReact.init
  );
  initialised = true;
}

/**
 * Attach the signed-in user's persona (no PII) so errors can be grouped by
 * persona without leaking name/email.
 */
export function setSentryUser(opts: {
  id: string;
  persona: "player" | "scout" | "academy" | null;
}): void {
  if (!initialised) return;
  Sentry.setUser({
    id: opts.id,
    // ip_address is implicit on the platform and gets PII-stripped by
    // Sentry's data scrubber; we explicitly drop name/email.
    segment: opts.persona ?? "unknown",
  });
}

export function clearSentryUser(): void {
  if (!initialised) return;
  Sentry.setUser(null);
}

/** React ErrorBoundary — re-export of the Sentry one for convenience. */
export const SentryErrorBoundary = SentryReact.ErrorBoundary;

/** Manually report an error (useful from catch-blocks where we already showed a toast). */
export function reportError(err: unknown, context?: Record<string, unknown>): void {
  if (!initialised) return;
  Sentry.captureException(err, context ? { extra: context } : undefined);
}
